import { Box, Divider, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { PreviewDebts } from "../../Models/ReferenceCodes";
import { ROUTE_PATHS } from "../../Routes/constants";
import { getPreviewSummary } from "../../Services/ReferceCodesService";
import { sendEvent } from "../../Utils/analytics";
import { getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import CToast from "../CToast/CToast";
import FormatAmount from "../FormatData/FormatAmount";
import FormatDateToTR from "../FormatData/FormatDate";
import "./PaymentDetail.scss";
import PaymentDetailDebt from "./PaymentDetailDebt";

const PaymentDetail = () => {
  const [loading, setLoading] = useState(true); // Yüklenme durumu
  const [visibleSwitch, setVisibleSwitch] = useState(true); // Yüklenme durumu
  const location = useLocation();
  const [snoozeDetails, setSnoozeDetails] = useState(location.state); // useState ile yönetilmeli
  const { paymentMethod, previewSummary, setPreviewSummary } = useAuthState();
  const navigate = useNavigate();

  useEffect(() => {
    handlePreviewSummary();
  }, []);

  const handlePreviewSummary = async () => {
    if (paymentMethod) {
      await getPreviewSummary(paymentMethod)
        .then((data) => {
          if (data?.isPaid) {
            CToast(getRemoteValue("pay_success_message"), "success");
            navigate(ROUTE_PATHS.DEFAULT_PAYMENTS);
          }
          data && setPreviewSummary(data);
          if (paymentMethod?.snoozeDetails) {
            setSnoozeDetails(paymentMethod.snoozeDetails);
          }
          sendEvent(`view_checkout_payment`, {
            total_payment_amount: data?.amount,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleContentDisplaySwitch = () => {
    setVisibleSwitch(!visibleSwitch);
  };

  // CRL-3241 "Toplu Öde / Tutar gir": a custom bulk amount overrides the preview total
  const payableAmount = paymentMethod?.amount ?? previewSummary?.amount;

  // CRL-3241: with a custom amount, list only the debts the amount reaches
  // (nearest-due allocation from the bulk pay modal); full list otherwise
  const customAllocation = paymentMethod?.amount
    ? paymentMethod?.customAllocation
    : undefined;
  const allPlans = previewSummary?.paymentPlans ?? [];
  const allocatedPlans = customAllocation
    ? allPlans.filter(
        (d) =>
          customAllocation.coveredCreditIds.includes(d.creditId) ||
          d.creditId === customAllocation.partial?.creditId
      )
    : allPlans;
  // guard against a stale/incomplete allocation not matching the preview
  const displayedPlans = allocatedPlans.length > 0 ? allocatedPlans : allPlans;
  return (
    <section id="checkout-detail" className="px-4 ml:py-8">
      <Stack
        sx={{
          borderRadius: "16px",
          border: "1px solid #F1F5F9",
          boxShadow: "0px 4px 17px 0px #F1F1F4",
        }}
        className="p-3 pb-4"
      >
        {loading ? (
          <Stack
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <ClipLoader
              color="#191B1E"
              size={35}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </Stack>
        ) : (
          <>
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              sx={{
                "@media (min-width: 900.5px)": {
                  pointerEvents: "none",
                },
              }}
              onClick={() => {
                handleContentDisplaySwitch();
              }}
            >
              <div>
                <div className=" text-[16px] md:text-[18px] font-semibold	px-3 pt-3">
                  {paymentMethod?.type === 3
                    ? getRemoteValue("snooze_detail")
                    : getRemoteValue("total_amount_paid")}
                </div>
                <div className="text-[#191B1E] text-[18px] md:text-[24px] font-bold pt-4  px-3">
                  {paymentMethod?.type === 3 ? snoozeDetails?.snoozeFee && FormatAmount(snoozeDetails?.snoozeFee) : (payableAmount &&
                    FormatAmount(payableAmount))}
                </div>
              </div>
              <Box
                component="img"
                className={`arrow-button close ${visibleSwitch ? "close" : "open"
                  } `}
                sx={{
                  width: "30px",
                  height: "30px",
                  marginRight: "18px",
                  "@media (min-width: 900.5px)": {
                    display: "none",
                  },
                }}
                alt="colendi"
                src="/icons/bottom-arrow.svg"
              />
            </Box>
            <Stack className={`${visibleSwitch ? "inVisibleForMobile" : ""}`}>
              <Divider className="!mt-6 px-3" sx={{ borderColor: "#E2E8F0" }} />
              {paymentMethod?.type === 3 ? (
                <Box className="p-3">
                  <Typography className="text-[15px] ml:text-[16px] mt-6 mb-3 font-semibold">
                    {getRemoteValue("snooze_details")}
                  </Typography>

                  <Stack spacing={2} sx={{ bgcolor: '#F8FAFC', p: 2, borderRadius: 1 }}>
                    <Box display="flex" alignItems="center">
                      <img
                        src={snoozeDetails?.merchantLogo}
                        alt={snoozeDetails?.merchantName}
                        style={{ objectFit: "cover", width: "22px", height: "22px" }}
                      />
                      <h2 className="text-[15px] lg:text-base font-semibold pl-4 content-center">
                        {snoozeDetails?.merchantName} {getRemoteValue("spending_suffix")}
                      </h2>
                    </Box>

                    <Box display="flex" justifyContent="space-between">
                      <Typography color="textSecondary">
                        {getRemoteValue("snooze_days")}
                      </Typography>
                      <Typography>
                        {snoozeDetails?.snoozeDays || '-'}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between">
                      <Typography color="textSecondary">
                        {getRemoteValue("new_payment_date")}
                      </Typography>
                      <Typography>
                        {FormatDateToTR(snoozeDetails?.newPaymentDate || '-')}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between">
                      <Typography color="textSecondary">
                        {getRemoteValue("snooze_fee")}
                      </Typography>
                      <Typography>
                        {snoozeDetails?.snoozeFee ? `${snoozeDetails.snoozeFee} TL` : '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ) : (
                <>
                  <div className="text-[15px] ml:text-[16px] mt-6 mb-3 font-semibold leading-[normal] px-3">
                    {getRemoteValue("payment_details")} (
                    {displayedPlans.length} {getRemoteValue("piece")})
                  </div>
                  <Stack className="p-3 scroll">
                    <Stack
                      sx={{
                        position: "relative",
                        gap: "16px",
                      }}
                    >
                      {displayedPlans.map((debt: PreviewDebts) => (
                        <PaymentDetailDebt
                          key={debt.creditId}
                          debt={debt}
                          partialPayAmount={
                            customAllocation?.partial &&
                            debt.creditId === customAllocation.partial.creditId
                              ? customAllocation.partial.amount
                              : undefined
                          }
                        />
                      ))}
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </section>
  );
};

export default PaymentDetail;
